import { html } from 'lit';

import { AdVendor } from '@client/core/AdVendor.js';
import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import {
  $retailMediaAtom,
  type AspectFormat
} from '@client/core/atoms/retailMedia.js';
import { extractRetailMediaParams } from '@client/core/utils/retailMediaUtils.js';
import { triggerCarouselRequest } from '@client/retailMedia/carousel/index.js';
import { triggerRequest } from '@client/retailMedia/index.js';
import {
  AdServer,
  AdSlot,
  DisplayPlacementId,
  type LayoutType,
  type PlacementId,
  PlacementStatus
} from '@schibsted-nmp/advertising-shared';

export type DisplaySettings = {
  layoutType?: 'grid' | 'list';
  aspectFormat?: AspectFormat;
};
// Module-level so it survives element remounts
const initializedPlacements = new Set<PlacementId>();
const lastProcessedAtom = new Map<PlacementId, string>();

export class RetailMediaAdVendor extends AdVendor {
  declare placementId: PlacementId;

  /** Layout and aspect format from the search page */
  displaySettings?: DisplaySettings;

  constructor(
    placementId: string,
    layoutType?: LayoutType,
    aspectFormat?: AspectFormat
  ) {
    super(AdServer.Retailmedia, placementId as DisplayPlacementId);
    if (layoutType || aspectFormat) {
      this.displaySettings = {};
      if (layoutType) this.displaySettings.layoutType = layoutType;
      if (aspectFormat) this.displaySettings.aspectFormat = aspectFormat;
    }
  }

  override updated() {
    // do nothing
  }

  private unsubscribe: (() => void) | null = null;

  override cleanupEvents() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  override setupEvents() {
    const { placementId } = this;
    const isFirstInstanceForPlacement = !initializedPlacements.has(placementId);

    this.unsubscribe = $retailMediaAtom.subscribe((atom) => {
      // Only the first instance per placement handles 'init'
      if (atom.eventType === 'init' && !isFirstInstanceForPlacement) {
        return;
      }

      // Single ad is advt_spl_middle_1; any other id is the carousel
      const isCarouselPlacement =
        placementId !== DisplayPlacementId.AdvtSplMiddle1;

      // Layout change remounts the carousel's banner, so re-request it (like GAM).
      // The single ad isn't remounted, so skip it.
      const isLayoutChange = atom.eventType === 'layout_change';
      if (isLayoutChange && !isCarouselPlacement) {
        return;
      }

      if (!isLayoutChange) {
        const atomKey = JSON.stringify({
          searchParams: atom.searchParams,
          eventType: atom.eventType
        });

        if (lastProcessedAtom.get(placementId) === atomKey) {
          updateMetricByKey(placementId, PlacementStatus.AdNoFill);
          updatePlacementKeyValueById(placementId, 'status', 'error');
          return;
        }

        lastProcessedAtom.set(placementId, atomKey);
      }

      const { searchQuery, categories } = extractRetailMediaParams(
        atom.searchParams
      );
      if (!categories && !searchQuery) {
        return;
      }
      // Carousel has its own request path (more products, no display settings)
      if (isCarouselPlacement) {
        triggerCarouselRequest(
          placementId,
          searchQuery,
          categories,
          atom.metadata?.brandNames
        );
      } else {
        triggerRequest(
          placementId,
          searchQuery,
          categories,
          this.displaySettings,
          atom.metadata?.brandNames
        );
      }
    });

    initializedPlacements.add(placementId);
  }

  override requestAd() {
    // Requests are triggered by the $retailMediaAtom subscription
  }

  override render() {
    return html`
      <slot
        name="${AdSlot.fromPlacementId(this.placementId)}"
        id="${this.placementId}"
      ></slot>
    `;
  }
}
